import { t } from "@lingui/core/macro"
import { isMobileDevice } from "@multimediallc/web-utils/modernizr"
import { EventRouter } from "../../../../common/events"
import { isEnterKey } from "../../../../common/eventsUtil"
import { addPageAction } from "../../../../common/newrelic"
import { dom } from "../../../../common/tsxrender/dom"
import { pageContext } from "../../../interfaces/context"
import { BaseSuggestedItem } from "../baseSuggestedItem"
import { ResultType, SearchBlur } from "../constants"
import { saveSearchHistory } from "../searchHistory"
import type { ISuggestionProps } from "../baseSuggestedItem"

export const MobileSearchSuggestionClicked = new EventRouter<void>("MobileSearchSuggestionClicked")

function translateSearchKeywords(search: string): string {
    return t`Search "${search}"`
}

interface HighlightParts {
    pre: string;
    match: string;
    post: string;
}


/**
 * Splits text into three parts based on a search term for highlighting.
 * @param fullText The complete text string.
 * @param searchText The text to find within fullText.
 * @param startIndex Optional index to start searching from. If -1 or omitted, searches from the beginning.
 * @returns An object with { pre, match, post } parts.
 */
export function getHighlightParts(fullText: string, searchText: string, startIndex: number = -1): HighlightParts {
    const searchLower = searchText.toLowerCase()
    const fullTextLower = fullText.toLowerCase()
    const index = startIndex >= 0
        ? fullTextLower.indexOf(searchLower, startIndex)
        : fullTextLower.indexOf(searchLower)

    // Handle case where the highlight should start at a specific index (e.g., after "Search \"")
    // but the actual match might occur later
    let effectiveIndex = index
    if (startIndex >= 0) {
        effectiveIndex = startIndex
    }

    if (effectiveIndex < 0) {
        return { pre: "", match: fullText, post: "" }
    }

    const pre = fullText.slice(0, effectiveIndex)
    const match = fullText.slice(effectiveIndex, effectiveIndex + searchText.length)
    const post = fullText.slice(effectiveIndex + searchText.length)
    return { pre, match, post }
}


export class MobileSearchPaneSuggestedItem extends BaseSuggestedItem {
    protected createLabelElements(props: ISuggestionProps): HTMLElement[] {
        const { labelText: originalLabelText, input: originalInput, resultType } = props

        let currentLabelText = originalLabelText
        let textToHighlight = originalInput
        let highlightStartIndex = -1

        if (resultType === ResultType.Keywords) {
            currentLabelText = translateSearchKeywords(originalInput)
            highlightStartIndex = translateSearchKeywords("").length - 1
            textToHighlight = originalInput
        } else {
            if (originalLabelText.startsWith("#")) {
                textToHighlight = originalInput.replace(/%23/g, "#")
            } else {
                textToHighlight = originalInput.replace(/#/g, "")
            }
        }

        const { pre, match, post } = getHighlightParts(currentLabelText, textToHighlight, highlightStartIndex)

        const regularStyleProps = { className: "regularText" }

        const elements: HTMLElement[] = []
        if (pre) {
            elements.push(<span className="boldText" >{pre}</span>)
        }
        if (match) {
            elements.push(<span {...regularStyleProps}>{match}</span>)
        }
        if (post) {
            elements.push(<span className="boldText" >{post}</span>)
        }

        elements.push(<div className="statusText">{this.getStatusText()}</div>)
        return elements
    }

    protected createElement(props: ISuggestionProps): HTMLAnchorElement {
        return <a
            href={props.href}
            tabIndex={-1}
            onKeyDown={(evt: KeyboardEvent) => {
                if (isEnterKey(evt)) {
                    // Tab, select, then enter is treated as a Search Result click.
                    this.onClickHandler(props)
                }
            }}
            onContextMenu={(event: MouseEvent) => {
                if (isMobileDevice() || pageContext.current.isMobile) {
                    event.preventDefault()
                    return false
                }
                return true
            }}
            onBlur={(evt: FocusEvent) => { SearchBlur.fire(evt) }}
            onClick={(evt: MouseEvent) => {
                if (props.href === "") {
                    evt.preventDefault()
                }
                this.onClickHandler(props, evt)
            }}>
            <div className={`${this.getRowClassName()} suggestionRow`}>
                <div className={`${this.getIconClassName()} suggestionIcon`}></div>
                <div className="labelContainer"><p className={`${this.getRowClassName()} suggestionLabel`}>
                    {this.createLabelElements(props)}
                </p></div>
            </div>
        </a>
    }

    protected onClickHandler(props: ISuggestionProps, evt?: MouseEvent): void {
        saveSearchHistory(props.labelText, ResultType.Online)
        const attributes = { "resultType": "broadcaster", "resultString": props.labelText }
        addPageAction("SearchBarResultsClick", attributes)
    }
}
